import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { FiCheck, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import styles from './ScreenshotThumbnailStrip.module.scss';

const VISIBLE_THUMBNAILS = 10;
const THUMBNAIL_WIDTH = 120;
const THUMBNAIL_GAP = 12;
const ITEM_SPAN = THUMBNAIL_WIDTH + THUMBNAIL_GAP;
const SCROLL_STEP = VISIBLE_THUMBNAILS * ITEM_SPAN - THUMBNAIL_GAP;
// Extra pages of thumbnails to keep loaded beyond the visible window, on
// each side, so paging/jumping never lands on an unloaded thumbnail.
const PRELOAD_PAGES = 1;

export const ScreenshotThumbnailStripComponent = ({
  items = [],
  selectedIndex,
  onSelect,
  className,
}) => {
  const trackRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [visibleIndexes, setVisibleIndexes] = useState(() => new Set());

  // Windowing: only mount <img> for thumbnails within (or near) the
  // visible scroll range, instead of every thumbnail in the session at
  // once. Revealed indexes are kept rendered (never re-hidden) to avoid
  // re-fetching on scroll-back.
  //
  // The range is computed directly from scroll geometry (scrollLeft /
  // clientWidth), not from IntersectionObserver — under rapid repeated
  // Next/Prev clicks, overlapping smooth-scroll animations can skip past
  // a thumbnail's "visible" moment between the browser's async
  // intersection checks, permanently leaving it unrevealed. Reading
  // scrollLeft synchronously and revealing proactively (before a
  // programmatic scroll even starts) has no such race.
  const revealRange = useCallback(
    (startIndex, endIndex) => {
      const from = Math.max(0, startIndex);
      const to = Math.min(items.length - 1, endIndex);
      if (from > to) return;
      setVisibleIndexes((prev) => {
        let changed = false;
        const next = new Set(prev);
        for (let i = from; i <= to; i += 1) {
          if (!next.has(i)) {
            next.add(i);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    },
    [items.length]
  );

  const revealAroundScrollLeft = useCallback(
    (scrollLeft, clientWidth) => {
      const preloadSpan = PRELOAD_PAGES * VISIBLE_THUMBNAILS * ITEM_SPAN;
      revealRange(
        Math.floor((scrollLeft - preloadSpan) / ITEM_SPAN),
        Math.ceil((scrollLeft + clientWidth + preloadSpan) / ITEM_SPAN)
      );
    },
    [revealRange]
  );

  const updateScrollState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanScrollPrev(track.scrollLeft > 1);
    setCanScrollNext(
      track.scrollLeft + track.clientWidth < track.scrollWidth - 1
    );
    revealAroundScrollLeft(track.scrollLeft, track.clientWidth);
  }, [revealAroundScrollLeft]);

  useEffect(() => {
    updateScrollState();
    const track = trackRef.current;
    if (!track) return;
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(track);
    return () => observer.disconnect();
  }, [items.length, updateScrollState]);

  useEffect(() => {
    revealRange(
      selectedIndex - VISIBLE_THUMBNAILS,
      selectedIndex + VISIBLE_THUMBNAILS
    );
    const track = trackRef.current;
    if (!track) return;
    const active = track.querySelector(`[data-index="${selectedIndex}"]`);
    if (!active) return;
    // Center the active thumbnail by setting track.scrollLeft directly
    // rather than active.scrollIntoView(). Near the first/last thumbnail
    // there's no room left within the track to fully center it, and
    // scrollIntoView's native behavior then climbs up to the next
    // scrollable ancestor — .yarl__container (overflow:hidden still
    // counts) — and scrolls that instead, panning the whole lightbox
    // viewport. Confining the scroll to the track avoids that entirely.
    const trackRect = track.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const activeCenter =
      activeRect.left -
      trackRect.left +
      track.scrollLeft +
      activeRect.width / 2;
    const targetScrollLeft = activeCenter - track.clientWidth / 2;
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const clampedScrollLeft = Math.min(
      maxScrollLeft,
      Math.max(0, targetScrollLeft)
    );
    track.scrollTo({ left: clampedScrollLeft, behavior: 'smooth' });
  }, [selectedIndex, revealRange]);

  const scrollByStep = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    // Snap to the next/previous page from wherever we currently are
    // (including mid-animation) instead of accumulating a raw pixel
    // delta — rapid repeated clicks land on a clamped, valid page rather
    // than drifting past the end from compounding offsets.
    const currentPage = Math.round(track.scrollLeft / SCROLL_STEP);
    const targetScrollLeft = Math.min(
      maxScrollLeft,
      Math.max(0, (currentPage + direction) * SCROLL_STEP)
    );
    revealAroundScrollLeft(targetScrollLeft, track.clientWidth);
    track.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  };

  if (!items.length) return null;

  return (
    <div className={classNames(styles.strip, className)}>
      <button
        type="button"
        className={styles.navButton}
        onClick={() => scrollByStep(-1)}
        disabled={!canScrollPrev}
        aria-label="Scroll thumbnails left"
      >
        <FiChevronLeft size={18} />
      </button>

      <div ref={trackRef} className={styles.track} onScroll={updateScrollState}>
        {items.map((item, index) => {
          const active = index === selectedIndex;
          const shouldRenderImage = active || visibleIndexes.has(index);
          return (
            <button
              key={item.id}
              type="button"
              data-index={index}
              className={classNames(styles.thumbnail, {
                [styles.thumbnail_active]: active,
              })}
              onClick={() => onSelect?.(index)}
              aria-label={item.label || `Screenshot ${index + 1}`}
              aria-current={active}
            >
              <span className={styles.imageWrapper}>
                {shouldRenderImage ? (
                  <img
                    src={item.previewSrc}
                    alt={item.label || `Screenshot ${index + 1}`}
                    className={styles.image}
                    loading="lazy"
                  />
                ) : (
                  <span className={styles.imagePlaceholder} />
                )}
                {active && (
                  <span className={styles.indicator}>
                    <FiCheck size={10} />
                  </span>
                )}
              </span>
              {item.label && (
                <Text
                  variant={textVariantsEnum.Text_xxs}
                  fontWeight={fontWeightEnum.Normal}
                  color="gray-500"
                  className={styles.label}
                >
                  {item.label}
                </Text>
              )}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className={styles.navButton}
        onClick={() => scrollByStep(1)}
        disabled={!canScrollNext}
        aria-label="Scroll thumbnails right"
      >
        <FiChevronRight size={18} />
      </button>
    </div>
  );
};

ScreenshotThumbnailStripComponent.displayName = 'ScreenshotThumbnailStrip';
